import React, { useState, useEffect } from 'react';
import { string, func, object, bool } from 'prop-types';
import {
  withStyles,
  MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useParams } from 'react-router-dom';
import FormSelect from '../components/FormSelect';
import FormText from '../components/FormText';

let styles = (theme) => ({
  component: {
    padding: theme.spacing(2),
    textAlign: 'left',
    color: theme.palette.text.secondary,
  },
  marginItem: {
    marginRight: theme.spacing(2),
  },
  barLayout: {
    padding: theme.spacing(1),
  },
  container1: {
    width: 800,
  },
  expirationContainer: {
    marginTop: theme.spacing(4),
  },
  rowContainer: {
    width: 600,
  },
  itemRowContainer: {
    width: 300,
    display: 'flex',
    justifyContent: 'center',
  },
  paymentButton: {
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: '#ffffff',
    backgroundColor: '#357a38',
  },
  container2: {
    borderLeftStyle: 'double',
    borderLeftWidth: 4,
    borderLeftColor: '#000000',
  },
  summaryMargin: {
    marginLeft: theme.spacing(2),
  },
  infoContainer: {
    marginLeft: theme.spacing(4),
  },
  paymentText: {
    marginTop: theme.spacing(2),
  },
  summaryText: {
    fontWeight: '500',
  },
  progress: {
    flexGrow: 1,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  buttonBack: {
    backgroundColor: '#0280CA',
    color: '#ffffff',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonBackTo: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fileInput: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '56px',
    alignItems: 'center',
  },
  file: {
    display: 'block',
  },
  fileLabel: {
    color: theme.palette.text.secondary,
  },
});

let theme = createTheme({
  palette: {
    primary: {
      main: '#6fbf73',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057',
      contrastText: '#ffffff',
    },
  },
  typography: {
    useNextVariants: true,
  },
});

function Condition({ when, is, children }) {
  return (
    <Field name={when} subscription={{ value: true }}>
      {({ input: { value } }) => (value === is ? children : null)}
    </Field>
  );
}

function QuoteField(props) {
  let { fee, quote, paymentMethodId, getQuoteRequest } = props;
  let { t } = useTranslation();
  let hasExpressFees = fee.expressFeeIsEnabled && fee.expressFeeAmount;

  useEffect(() => {
    if ((fee.amount || hasExpressFees) && paymentMethodId) {
      getQuoteRequest(fee, paymentMethodId);
    }
  }, [paymentMethodId, getQuoteRequest, fee]);

  if (quote) {
    let {
      serviceFee,
      serviceFeeCurrency,
      totalSystemCur,
      systemCurrency,
      totalPaymentCur,
      currency,
    } = quote;
    return (
      <React.Fragment>
        {Boolean(quote.serviceFee) && (
          <span>
            {t('serviceFee', {
              amount: serviceFee,
              currency: serviceFeeCurrency,
            })}
          </span>
        )}
        <span>
          {t('costOfVisa', {
            amount: totalSystemCur,
            currency: systemCurrency,
          })}
        </span>
        <span>
          {t('totalPaymentAmount', { amount: totalPaymentCur, currency })}
        </span>
      </React.Fragment>
    );
  }
  return '';
}

function getPaymentMethodsMap(paymentMethods) {
  return paymentMethods.reduce((map, method) => {
    map[method.label] = method;
    return map;
  }, {});
}

function PaymentCard(props) {
  let {
    classes,
    readOnly,
    firstName = ' ',
    lastName = ' ',
    passportNumber = ' ',
    quote = null,
    isFetching = false,
    fee,
    isLAO,
    visatypeId,
    paymentRecord,
    paymentMethods,
    getQuoteRequest,
    getFeesRequest,
    getPaymentMethods,
    submitPaymentRequest,
    performCashPaymentFlow,
    performMockPaymentFlow,
    sendPaymentLink,
    markForLocalPayment,
    getApplication,
  } = props;
  let { t } = useTranslation();
  let paymentMethodsMap = getPaymentMethodsMap(paymentMethods);

  let cashId = (paymentMethodsMap['Cash'] || {}).value;
  let mockId = (paymentMethodsMap['Mock'] || {}).value;
  let otherId = (paymentMethodsMap['Other'] || {}).value;
  let localPaymentId = 'PAY_AT_CLOSEST_EMBASSY';
  let methodIdsWithoutQuote = [otherId, localPaymentId];
  let { push } = useHistory();
  let [file, setFile] = useState();
  let { uuid } = useParams();

  useEffect(() => {
    if (!firstName && uuid) {
      getApplication(uuid);
    }
  }, [getApplication, firstName, uuid]);

  useEffect(() => {
    if (!fee.amount) {
      getFeesRequest(visatypeId);
    }
    if (!paymentMethods.length) {
      getPaymentMethods(isLAO);
    }
  }, [
    fee.amount,
    getFeesRequest,
    getPaymentMethods,
    isLAO,
    paymentMethods.length,
    visatypeId,
  ]);

  useEffect(() => {
    if (!paymentRecord) return;
    if (paymentRecord.redirectUrls) {
      window.location.replace(paymentRecord.redirectUrls);
    } else {
      submitPaymentRequest(paymentRecord.uuid, 'APPROVED');
      push(`/payment/${uuid}/success`);
    }
  }, [paymentRecord, submitPaymentRequest, push, uuid]);

  function onSubmit(values) {
    switch (values.paymentMethodId) {
      case mockId:
        return performMockPaymentFlow(quote.uuid, uuid, push);
      case otherId:
        return sendPaymentLink(uuid);
      case localPaymentId:
        return markForLocalPayment(uuid, push);
      default:
        return performCashPaymentFlow(
          quote.uuid,
          file,
          values.cashPaymentReceiptNum,
          `${window.location.href}/success`,
          `${window.location.href}/failure`,
          `${window.location.href}`
        );
    }
  }

  function onFileUpload(e) {
    let files = e.target.files;
    if (!files || !files.length) return;
    setFile(files[0]);
  }

  return (
    <div>
      <Typography
        className={classes.barLayout}
        style={{ color: '#ffffff', background: '#4CAF50' }}
        variant="subtitle1"
        align="left"
        gutterBottom
      >
        {t('secureCardPayment')}
      </Typography>
      {isFetching && <LinearProgress className={classes.progress} />}
      <Grid item xs={12}>
        <Form onSubmit={onSubmit}>
          {({ handleSubmit, values }) => (
            <Grid container direction="row">
              <Grid className={classes.container1} item xs={6}>
                <Grid className={classes.marginItem}>
                  <Field
                    name="paymentMethodId"
                    className={classes.component}
                    label={t('paymentMethod')}
                    readOnly={readOnly}
                  >
                    {(props) => (
                      <FormSelect
                        value={props.input.value}
                        onChange={props.input.onChange}
                        options={paymentMethods}
                        {...props}
                      />
                    )}
                  </Field>
                </Grid>
                <Condition when="paymentMethodId" is={cashId}>
                  <div>
                    <Grid className={classes.marginItem}>
                      <div className={classes.fileInput}>
                        <label className={classes.fileLabel} htmlFor="file">
                          {t('uploadPaymentReceiptScan')}
                        </label>
                        <input
                          id="file"
                          type="file"
                          name="file"
                          onChange={(e) => onFileUpload(e)}
                          className={classes.file}
                        />
                      </div>
                    </Grid>
                    <Grid className={classes.marginItem}>
                      <Field
                        name="cashPaymentReceiptNum"
                        component={FormText}
                        label={t('paymentReceiptNumber')}
                        className={classes.component}
                      />
                    </Grid>
                  </div>
                </Condition>

                <Grid
                  container
                  alignItems="center"
                  justifyContent="flex-end"
                  direction="row"
                >
                  <MuiThemeProvider theme={theme}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.paymentButton}
                      disabled={
                        isFetching ||
                        (quote === null &&
                          !methodIdsWithoutQuote.includes(
                            values.paymentMethodId
                          )) ||
                        (values.paymentMethodId === cashId && !file)
                      }
                      onClick={handleSubmit}
                    >
                      {values.paymentMethodId === otherId
                        ? t('sendPaymentLink')
                        : t('submitPayment')}
                    </Button>
                  </MuiThemeProvider>
                </Grid>
              </Grid>

              <Grid className={classes.container2} item xs={6}>
                <Grid className={classes.summaryMargin}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('summary')}
                  </Typography>
                  <Grid className={classes.infoContainer}>
                    <Grid>
                      <Typography
                        className={classes.summaryText}
                        variant="subtitle1"
                        gutterBottom
                      >
                        {t('firstName')}
                      </Typography>
                      <Typography variant="subtitle1" gutterBottom>
                        {firstName}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography
                        className={classes.summaryText}
                        variant="subtitle1"
                        gutterBottom
                      >
                        {t('familyName')}
                      </Typography>
                      <Typography variant="subtitle1" gutterBottom>
                        {lastName}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography
                        className={classes.summaryText}
                        variant="subtitle1"
                        gutterBottom
                      >
                        {t('passportNumber')}
                      </Typography>
                      <Typography variant="subtitle1" gutterBottom>
                        {passportNumber}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography
                    className={classes.paymentText}
                    variant="subtitle1"
                    gutterBottom
                  >
                    {t('payment')}
                  </Typography>
                  <Grid className={classes.infoContainer}>
                    <Grid>
                      <Typography
                        className={classes.summaryText}
                        variant="subtitle1"
                        gutterBottom
                      >
                        {t('totalAmount')}
                      </Typography>
                      <Typography variant="subtitle1" gutterBottom>
                        <Grid container direction="column">
                          <QuoteField
                            fee={fee}
                            quote={quote}
                            getQuoteRequest={getQuoteRequest}
                            paymentMethodId={
                              methodIdsWithoutQuote.includes(
                                values.paymentMethodId
                              )
                                ? null
                                : values.paymentMethodId
                            }
                          />
                        </Grid>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Form>
      </Grid>
      <Grid container justifyContent="flex-start">
        {isLAO && (
          <MuiThemeProvider theme={theme}>
            <Button
              variant="contained"
              color="primary"
              disabled={isFetching}
              className={classes.buttonBackTo}
              component={Link}
              to={'/applications'}
            >
              {t('backTo')}
            </Button>
          </MuiThemeProvider>
        )}
        <Grid item>
          <Button
            component={Link}
            to={isLAO ? '/view' : '/declaration'}
            variant="contained"
            color="secondary"
            className={classes.buttonBack}
            disabled={isFetching}
          >
            {t('back')}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
}

PaymentCard.propTypes = {
  classes: object.isRequired,
  firstName: string,
  lastName: string,
  passportNumber: string,
  quote: object,
  isFetching: bool,
  error: string,
  pay: func,
};

export default withStyles(styles)(PaymentCard);



// WEBPACK FOOTER //
// ./src/view/forms/Payment/PaymentCard.js